import React, { useState, useEffect } from 'react';
import { propertiesAPI } from '../services/api';
import PropertyLiabilities from '../components/PropertyLiabilities';
import { formatApiError } from '../utils/errorMessage';
import { parseCalendarDay } from '../utils/localDate';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error?.name, errorInfo?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">
                {this.props.section} Error
              </h3>
              <p className="text-sm text-red-700 mt-1">
                Something went wrong in this section
              </p>
              {this.props.onRetry && (
                <button
                  onClick={() => {
                    this.setState({ hasError: false, error: null });
                    this.props.onRetry();
                  }}
                  className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// PropertyRow component with error handling
const PropertyRow = ({ property, onEdit, onDelete, onViewLiabilities, onViewProfile }) => {
  const [rowError, setRowError] = useState(null);
  
  const handleEditClick = () => {
    try {
      onEdit(property);
    } catch (error) {
      console.error('Error editing property:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      setRowError('Failed to open edit form');
    }
  };
  
  const handleDeleteClick = async () => {
    try {
      const propertyId = property.id || property.property_id;
      await onDelete(propertyId);
    } catch (error) {
      console.error('Error deleting property:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      setRowError('Failed to delete property');
    }
  };

  const handleLiabilitiesClick = () => {
    try {
      onViewLiabilities(property);
    } catch (error) {
      console.error('Error viewing liabilities:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      setRowError('Failed to open liabilities');
    }
  };

  const handleProfileClick = () => {
    try {
      onViewProfile(property);
    } catch (error) {
      console.error('Error viewing profile:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      setRowError('Failed to open profile card');
    }
  };
  
  if (rowError) {
    return (
      <tr>
        <td colSpan="11" className="px-6 py-4">
          <div className="text-red-600 text-sm">
            {rowError}
            <button 
              onClick={() => setRowError(null)}
              className="ml-2 text-blue-600 hover:text-blue-800"
            >
              Dismiss
            </button>
          </div>
        </td>
      </tr>
    );
  }
  
  return (
    <ErrorBoundary section="Property Row">
      <tr className="hover:bg-gray-50">
        <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
          <ErrorBoundary section="Property Name">
            <div className="font-medium text-gray-900 text-sm sm:text-base">{property.property_name || 'Unnamed Property'}</div>
            <div className="text-xs sm:text-sm text-gray-500">{property.ownership_type || 'N/A'}</div>
          </ErrorBoundary>
        </td>
        <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-xs sm:text-sm text-center text-gray-900 hidden sm:table-cell">
          <ErrorBoundary section="Purchase Date">
            {property.purchase_date ? parseCalendarDay(property.purchase_date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : 'N/A'}
          </ErrorBoundary>
        </td>
        <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-900 capitalize">
          <ErrorBoundary section="Property Type">
            {property.property_type?.replace('_', ' ') || 'N/A'}
          </ErrorBoundary>
        </td>
        <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-900 hidden md:table-cell">
          <ErrorBoundary section="Address">
            <div>{property.street_address || 'N/A'}</div>
            <div className="text-gray-500">
              {property.city && property.state 
                ? `${property.city}, ${property.state}${property.zip_code ? ' ' + property.zip_code : ''}`
                : 'Address not provided'}
            </div>
          </ErrorBoundary>
        </td>
        <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-xs sm:text-sm text-center text-gray-900 hidden lg:table-cell">
          <ErrorBoundary section="Year Built">
            {property.year_built || 'N/A'}
          </ErrorBoundary>
        </td>
        <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-xs sm:text-sm text-center text-gray-900 hidden lg:table-cell">
          <ErrorBoundary section="Bed/Bath">
            <div>{property.bedrooms || '0'} / {property.bathrooms || '0'}</div>
          </ErrorBoundary>
        </td>
        <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-xs sm:text-sm text-right text-gray-900 hidden xl:table-cell">
          <ErrorBoundary section="Purchase Price">
            {property.purchase_price ? `$${parseFloat(property.purchase_price).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : 'N/A'}
          </ErrorBoundary>
        </td>
        <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-xs sm:text-sm text-right text-gray-900">
          <ErrorBoundary section="Current Value">
            ${parseFloat(property.current_value || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </ErrorBoundary>
        </td>
        <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-xs sm:text-sm text-right text-gray-900 hidden md:table-cell">
          <ErrorBoundary section="Monthly Rent">
            {property.monthly_rent ? `$${parseFloat(property.monthly_rent).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : 'N/A'}
          </ErrorBoundary>
        </td>
        <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
          <ErrorBoundary section="Status">
            <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
              property.property_status === 'active' ? 'bg-green-100 text-green-800' :
              property.property_status === 'rented' ? 'bg-blue-100 text-blue-800' :
              property.property_status === 'vacant' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {property.property_status?.toUpperCase() || 'UNKNOWN'}
            </span>
          </ErrorBoundary>
        </td>
        <td className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 text-xs sm:text-sm">
          <ErrorBoundary section="Actions">
            <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
              <button
                onClick={handleDeleteClick}
                className="text-red-600 hover:text-red-800 sm:mr-2 md:mr-3"
              >
                Delete
              </button>
              <button
                onClick={handleLiabilitiesClick}
                className="text-purple-600 hover:text-purple-800 font-medium"
                title="View Liabilities"
              >
                💰 Liabilities
              </button>
              <button
                onClick={handleEditClick}
                className="text-blue-600 hover:text-blue-800"
              >
                Edit
              </button>
              <button
                onClick={handleProfileClick}
                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded font-medium transition-colors shadow-sm"
                title="View Property Profile"
              >
                🏠 Profile
              </button>
            </div>
          </ErrorBoundary>
        </td>
      </tr>
    </ErrorBoundary>
  );
};

const Properties = ({ selectedBusiness }) => {
  // Separate states for each section
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState(null);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [propertiesError, setPropertiesError] = useState(null);
  const [statsError, setStatsError] = useState(null);
  
  // Form-level error states
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [showModal, setShowModal] = useState(false);
  const [showLiabilities, setShowLiabilities] = useState(false);
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [filters, setFilters] = useState({
    property_status: '',
    property_type: ''
  });
  const [formData, setFormData] = useState({
    property_name: '',
    property_type: 'residential',
    property_status: 'active',
    ownership_type: 'owned',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    square_footage: '',
    bedrooms: '',
    bathrooms: '',
    year_built: '',
    purchase_date: '',
    purchase_price: '',
    current_value: '',
    monthly_rent: '',
    mortgage_balance: '',
    mortgage_payment: '',
    mortgage_lender: '',
    mortgage_rate: '',
    photo_url: '',
    notes: ''
  });

  useEffect(() => {
    loadProperties();
    loadStats();
  }, [selectedBusiness, filters]);

  // Independent loading function for properties
  const loadProperties = async () => {
    try {
      setPropertiesLoading(true);
      setPropertiesError(null);
      const params = {
        ...(selectedBusiness && { business_id: selectedBusiness }),
        ...filters
      };
      
      const propertiesRes = await propertiesAPI.getAll(params);
      setProperties(propertiesRes.data || []);
    } catch (error) {
      console.error('Failed to load properties:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      setPropertiesError(formatApiError(error, 'Failed to load properties'));
      setProperties([]);
    } finally {
      setPropertiesLoading(false);
    }
  };

  // Independent loading function for stats
  const loadStats = async () => {
    try {
      setStatsLoading(true);
      setStatsError(null);
      const params = {
        ...(selectedBusiness && { business_id: selectedBusiness }),
        ...filters
      };
      
      const statsRes = await propertiesAPI.getSummary(params);
      setStats(statsRes.data || null);
    } catch (error) {
      console.error('Failed to load stats:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      setStatsError(formatApiError(error, 'Failed to load statistics'));
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset errors
    setFormError(null);
    setFieldErrors({});
    setIsSubmitting(true);
    
    try {
      // Validate required fields
      const errors = {};
      if (!formData.property_name?.trim()) {
        errors.property_name = 'Property name is required';
      }
      if (!formData.street_address?.trim()) {
        errors.street_address = 'Address is required';
      }
      if (!formData.city?.trim()) {
        errors.city = 'City is required';
      }
      if (!formData.state?.trim()) {
        errors.state = 'State is required';
      }
      
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setFormError('Please fix the validation errors');
        setIsSubmitting(false);
        return;
      }

      const dataToSubmit = {
        ...formData,
        business_id: selectedBusiness || null,
        // Convert empty strings to null for numeric fields
        square_footage: formData.square_footage === '' ? null : formData.square_footage,
        bedrooms: formData.bedrooms === '' ? null : formData.bedrooms,
        bathrooms: formData.bathrooms === '' ? null : formData.bathrooms,
        year_built: formData.year_built === '' ? null : formData.year_built,
        purchase_price: formData.purchase_price === '' ? null : formData.purchase_price,
        current_value: formData.current_value === '' ? null : formData.current_value,
        monthly_rent: formData.monthly_rent === '' ? null : formData.monthly_rent,
        mortgage_balance: formData.mortgage_balance === '' ? null : formData.mortgage_balance,
        mortgage_payment: formData.mortgage_payment === '' ? null : formData.mortgage_payment,
        mortgage_rate: formData.mortgage_rate === '' ? null : formData.mortgage_rate,
        photo_url: formData.photo_url === '' ? null : formData.photo_url,
      };

      let result;
      if (editingProperty) {
        const propertyId = editingProperty.id || editingProperty.property_id;
        console.log('Updating property ID:', propertyId);
        if (!propertyId) {
          throw new Error('Property ID is missing');
        }
        result = await propertiesAPI.update(propertyId, dataToSubmit);
      } else {
        result = await propertiesAPI.create(dataToSubmit);
      }
      
      setShowModal(false);
      setEditingProperty(null);
      resetForm();
      
      // Reload both sections independently with their own error handling
      loadProperties().catch(err => {
        console.error('Failed to reload properties after save:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
        setPropertiesError('Properties saved but failed to reload list');
      });
      
      loadStats().catch(err => {
        console.error('Failed to reload stats after save:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
        setStatsError('Properties saved but failed to reload stats');
      });
      
    } catch (error) {
      console.error('Failed to save property:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      setFormError(formatApiError(error, 'Failed to save property'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      property_name: '',
      property_type: 'residential',
      property_status: 'active',
      ownership_type: 'owned',
      street_address: '',
      city: '',
      state: '',
      zip_code: '',
      square_footage: '',
      bedrooms: '',
      bathrooms: '',
      year_built: '',
      purchase_date: '',
      purchase_price: '',
      current_value: '',
      monthly_rent: '',
      mortgage_balance: '',
      mortgage_payment: '',
      mortgage_lender: '',
      mortgage_rate: '',
      photo_url: '',
      notes: ''
    });
    setFormError(null);
    setFieldErrors({});
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      property_name: property.property_name || '',
      property_type: property.property_type || 'residential',
      property_status: property.property_status || 'active',
      ownership_type: property.ownership_type || 'owned',
      street_address: property.street_address || '',
      city: property.city || '',
      state: property.state || '',
      zip_code: property.zip_code || '',
      square_footage: property.square_footage || '',
      bedrooms: property.bedrooms || '',
      bathrooms: property.bathrooms || '',
      year_built: property.year_built || '',
      purchase_date: property.purchase_date?.split('T')[0] || '',
      purchase_price: property.purchase_price || '',
      current_value: property.current_value || '',
      monthly_rent: property.monthly_rent || '',
      mortgage_balance: property.mortgage_balance || '',
      mortgage_payment: property.mortgage_payment || '',
      mortgage_lender: property.mortgage_lender || '',
      mortgage_rate: property.mortgage_rate || '',
      photo_url: property.photo_url || '',
      notes: property.notes || ''
    });
    setShowModal(true);
  };

  const handleViewLiabilities = (property) => {
    setSelectedProperty(property);
    setShowLiabilities(true);
  };

  const handleViewProfile = (property) => {
    setSelectedProperty(property);
    setShowProfileCard(true);
  };

  const handleDelete = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    
    try {
      await propertiesAPI.delete(propertyId);
      
      // Reload both sections independently with their own error handling
      loadProperties().catch(err => {
        console.error('Failed to reload properties after delete:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
        setPropertiesError('Property deleted but failed to reload list');
      });
      
      loadStats().catch(err => {
        console.error('Failed to reload stats after delete:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
        setStatsError('Property deleted but failed to reload stats');
      });
      
    } catch (error) {
      console.error('Failed to delete property:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert(formatApiError(error, 'Failed to delete property'));
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Properties</h1>
            <p className="text-gray-600 mt-1">Manage real estate and rental properties</p>
          </div>
          <button
            onClick={() => {
              setEditingProperty(null);
              resetForm();
              setShowModal(true);
            }}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            + Add Property
          </button>
        </div>

        {/* Stats Cards - Independent Error Boundary */}
        <ErrorBoundary section="Statistics" onRetry={loadStats}>
          {statsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white p-4 rounded-lg shadow animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          ) : statsError ? (
            <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-yellow-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-yellow-800">Statistics unavailable: {statsError}</span>
                </div>
                <button
                  onClick={loadStats}
                  className="text-sm font-medium text-yellow-600 hover:text-yellow-500"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : stats ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-sm text-gray-600">Total Properties</div>
                <div className="text-2xl font-bold text-gray-800">{stats.total_properties || 0}</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-sm text-gray-600">Active Properties</div>
                <div className="text-2xl font-bold text-green-600">{stats.active_properties || 0}</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-sm text-gray-600">Total Value</div>
                <div className="text-2xl font-bold text-blue-600">
                  ${parseFloat(stats.total_value || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-sm text-gray-600">Monthly Rent Income</div>
                <div className="text-2xl font-bold text-purple-600">
                  ${parseFloat(stats.total_monthly_rental_income || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </div>
              </div>
            </div>
          ) : null}
        </ErrorBoundary>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select
              value={filters.property_status}
              onChange={(e) => setFilters({ ...filters, property_status: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="rented">Rented</option>
              <option value="vacant">Vacant</option>
              <option value="sold">Sold</option>
            </select>

            <select
              value={filters.property_type}
              onChange={(e) => setFilters({ ...filters, property_type: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2"
            >
              <option value="">All Types</option>
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
              <option value="land">Land</option>
              <option value="multi_family">Multi-Family</option>
            </select>
          </div>
        </div>
      </div>

      {/* Properties Table - Independent Error Boundary */}
      <ErrorBoundary section="Properties List" onRetry={loadProperties}>
        {propertiesLoading ? (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-4 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        ) : propertiesError ? (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Failed to load properties</h3>
              <p className="mt-1 text-sm text-gray-500" role="alert">{propertiesError}</p>
              <div className="mt-6">
                <button
                  onClick={loadProperties}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Retry Loading Properties
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property</th>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Purchase Date</th>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Address</th>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Year Built</th>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Bed/Bath</th>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">Purchase Price</th>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Monthly Rent</th>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {!properties || properties.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-4 text-center text-gray-500">
                      No properties found. Click "Add Property" to create one.
                    </td>
                  </tr>
                ) : (
                  properties.map((property) => (
                    <ErrorBoundary key={`eb-${property.id}`} section="Property Row">
                      <PropertyRow 
                        key={property.id}
                        property={property}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onViewLiabilities={handleViewLiabilities}
                        onViewProfile={handleViewProfile}
                      />
                    </ErrorBoundary>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </ErrorBoundary>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingProperty ? 'Edit Property' : 'Add New Property'}
            </h2>
            
            {formError && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" role="alert">
                <p className="font-medium">Error</p>
                <p className="text-sm">{formError}</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Property Name *</label>
                  <input
                    type="text"
                    value={formData.property_name}
                    onChange={(e) => {
                      setFormData({ ...formData, property_name: e.target.value });
                      if (fieldErrors.property_name) {
                        setFieldErrors({ ...fieldErrors, property_name: null });
                      }
                    }}
                    className={`w-full border rounded px-3 py-2 ${
                      fieldErrors.property_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Main Street Apartment"
                  />
                  {fieldErrors.property_name && (
                    <p className="text-red-500 text-sm mt-1">{fieldErrors.property_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
                  <select
                    value={formData.property_type}
                    onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="land">Land</option>
                    <option value="multi_family">Multi-Family</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.property_status}
                    onChange={(e) => setFormData({ ...formData, property_status: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="active">Active</option>
                    <option value="rented">Rented</option>
                    <option value="vacant">Vacant</option>
                    <option value="sold">Sold</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ownership Type</label>
                  <select
                    value={formData.ownership_type}
                    onChange={(e) => setFormData({ ...formData, ownership_type: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="owned">Owned</option>
                    <option value="mortgaged">Mortgaged</option>
                    <option value="rented">Rented</option>
                    <option value="leased">Leased</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
                  <input
                    type="text"
                    value={formData.street_address}
                    onChange={(e) => {
                      setFormData({ ...formData, street_address: e.target.value });
                      if (fieldErrors.street_address) {
                        setFieldErrors({ ...fieldErrors, street_address: null });
                      }
                    }}
                    className={`w-full border rounded px-3 py-2 ${
                      fieldErrors.street_address ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="123 Main St"
                  />
                  {fieldErrors.street_address && (
                    <p className="text-red-500 text-sm mt-1">{fieldErrors.street_address}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => {
                      setFormData({ ...formData, city: e.target.value });
                      if (fieldErrors.city) {
                        setFieldErrors({ ...fieldErrors, city: null });
                      }
                    }}
                    className={`w-full border rounded px-3 py-2 ${
                      fieldErrors.city ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {fieldErrors.city && (
                    <p className="text-red-500 text-sm mt-1">{fieldErrors.city}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => {
                      setFormData({ ...formData, state: e.target.value });
                      if (fieldErrors.state) {
                        setFieldErrors({ ...fieldErrors, state: null });
                      }
                    }}
                    className={`w-full border rounded px-3 py-2 ${
                      fieldErrors.state ? 'border-red-500' : 'border-gray-300'
                    }`}
                    maxLength="2"
                  />
                  {fieldErrors.state && (
                    <p className="text-red-500 text-sm mt-1">{fieldErrors.state}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                  <input
                    type="text"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Square Footage</label>
                  <input
                    type="number"
                    value={formData.square_footage}
                    onChange={(e) => setFormData({ ...formData, square_footage: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bedrooms</label>
                  <input
                    type="number"
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bathrooms</label>
                  <input
                    type="number"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    min="0"
                    step="0.5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year Built</label>
                  <input
                    type="number"
                    value={formData.year_built}
                    onChange={(e) => setFormData({ ...formData, year_built: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    min="1800"
                    max="2030"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
                  <input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>

                {/* Show Purchase Price for Owned/Mortgaged */}
                {(formData.ownership_type === 'owned' || formData.ownership_type === 'mortgaged') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Purchase Price
                      <span className="ml-1 text-xs text-gray-500">(Original cost)</span>
                    </label>
                    <input
                      type="number"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                    />
                  </div>
                )}

                {/* Show Down Payment for Leased */}
                {formData.ownership_type === 'leased' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Down Payment
                      <span className="ml-1 text-xs text-gray-500">(Initial payment)</span>
                    </label>
                    <input
                      type="number"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Value
                    <span className="ml-1 text-xs text-blue-600">(Market value - counted as Asset)</span>
                  </label>
                  <input
                    type="number"
                    value={formData.current_value}
                    onChange={(e) => setFormData({ ...formData, current_value: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>

                {/* Mortgage Information - show only for Mortgaged */}
                {formData.ownership_type === 'mortgaged' && (
                  <>
                    <div className="col-span-2 mt-4 mb-2">
                      <h3 className="text-md font-semibold text-gray-800 border-b pb-2">💳 Mortgage Information</h3>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mortgage Balance
                        <span className="ml-1 text-xs text-red-600">(Liability)</span>
                      </label>
                      <input
                        type="number"
                        value={formData.mortgage_balance || ''}
                        onChange={(e) => setFormData({ ...formData, mortgage_balance: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Monthly Mortgage Payment
                      </label>
                      <input
                        type="number"
                        value={formData.mortgage_payment || ''}
                        onChange={(e) => setFormData({ ...formData, mortgage_payment: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mortgage Lender
                      </label>
                      <input
                        type="text"
                        value={formData.mortgage_lender || ''}
                        onChange={(e) => setFormData({ ...formData, mortgage_lender: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        placeholder="Bank name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Interest Rate (%)
                      </label>
                      <input
                        type="number"
                        value={formData.mortgage_rate || ''}
                        onChange={(e) => setFormData({ ...formData, mortgage_rate: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="0.00"
                      />
                    </div>
                  </>
                )}

                {/* Rental/Lease Information */}
                <div className="col-span-2 mt-4 mb-2">
                  <h3 className="text-md font-semibold text-gray-800 border-b pb-2">💰 Income Information</h3>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monthly Rent Income
                    <span className="ml-2 text-xs text-green-600 font-normal">(Tracked as Income)</span>
                  </label>
                  <input
                    type="number"
                    value={formData.monthly_rent}
                    onChange={(e) => setFormData({ ...formData, monthly_rent: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Expected monthly rental income from this property
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Property Photo URL (Web Image Link)
                    <span className="ml-2 text-xs text-blue-600 font-normal">(Shows in profile card)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.photo_url || ''}
                    onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="https://photos.example.com/image.webp or .jpg or .png"
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Web image link (supports .jpg, .png, .webp, etc.)
                  </p>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    rows="3"
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingProperty(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 text-white rounded ${
                    isSubmitting 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {isSubmitting ? 'Saving...' : (editingProperty ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Property Liabilities Modal */}
      {showLiabilities && selectedProperty && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold">
                Property Liabilities - {selectedProperty.property_name}
              </h2>
              <button
                onClick={() => {
                  setShowLiabilities(false);
                  setSelectedProperty(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <PropertyLiabilities 
                propertyId={selectedProperty.id}
                propertyName={selectedProperty.property_name}
                selectedBusiness={selectedBusiness}
              />
            </div>
          </div>
        </div>
      )}

      {/* Property Profile Card Modal - Government ID Style */}
      {showProfileCard && selectedProperty && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="relative bg-gradient-to-br from-blue-50 to-gray-50 rounded-xl shadow-2xl max-w-4xl w-full border-4 border-blue-900">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowProfileCard(false);
                setSelectedProperty(null);
              }}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* ID Card Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-8 py-4 rounded-t-xl border-b-4 border-yellow-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-yellow-500 rounded-full p-2">
                    <svg className="w-8 h-8 text-blue-900" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider">Property Profile</h3>
                    <p className="text-xs text-blue-200">Property Details Card</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-blue-200">ID No.</p>
                  <p className="text-lg font-bold tracking-wider">#{selectedProperty.id}</p>
                </div>
              </div>
            </div>

            {/* Main ID Card Body */}
            <div className="p-8">
              <div className="grid grid-cols-3 gap-6">
                {/* Left Column - Photo */}
                <div className="col-span-1">
                  <div className="bg-white rounded-lg border-4 border-gray-300 overflow-hidden shadow-lg">
                    {selectedProperty.photo_url ? (
                      <img 
                        src={selectedProperty.photo_url} 
                        alt={selectedProperty.property_name}
                        className="w-full h-64 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={selectedProperty.photo_url ? "hidden" : "flex w-full h-64 bg-gradient-to-br from-gray-200 to-gray-300 items-center justify-center"}>
                      <div className="text-center text-gray-500">
                        <svg className="w-20 h-20 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                        <p className="text-xs font-semibold uppercase">No Photo</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Quick Stats Below Photo */}
                  <div className="mt-4 bg-white rounded-lg border-2 border-gray-300 p-3 shadow">
                    <div className="grid grid-cols-2 gap-2 text-center">
                      <div className="border-r border-gray-300">
                        <p className="text-2xl font-bold text-blue-900">{selectedProperty.bedrooms || '0'}</p>
                        <p className="text-xs text-gray-600 uppercase">Beds</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-900">{selectedProperty.bathrooms || '0'}</p>
                        <p className="text-xs text-gray-600 uppercase">Baths</p>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-300 text-center">
                      <p className="text-xl font-bold text-blue-900">
                        {selectedProperty.square_footage ? parseInt(selectedProperty.square_footage).toLocaleString() : 'N/A'}
                      </p>
                      <p className="text-xs text-gray-600 uppercase">Square Feet</p>
                    </div>
                  </div>
                </div>

                {/* Right Column - Property Details */}
                <div className="col-span-2 space-y-4">
                  {/* Property Name */}
                  <div className="bg-white rounded-lg border-2 border-blue-900 p-4 shadow-md">
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Property Name</p>
                    <h2 className="text-2xl font-bold text-blue-900 uppercase tracking-wide">{selectedProperty.property_name}</h2>
                  </div>

                  {/* Address Section */}
                  <div className="bg-white rounded-lg border-2 border-gray-300 p-4 shadow">
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">Property Address</p>
                    <div className="space-y-1">
                      <p className="text-base font-semibold text-gray-900">{selectedProperty.street_address}</p>
                      <p className="text-base text-gray-700">
                        {selectedProperty.city}, {selectedProperty.state} {selectedProperty.zip_code}
                      </p>
                    </div>
                  </div>

                  {/* Key Details Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-lg border-2 border-gray-300 p-3 shadow">
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Type</p>
                      <p className="text-sm font-bold text-gray-900 capitalize">{selectedProperty.property_type || 'N/A'}</p>
                    </div>
                    <div className="bg-white rounded-lg border-2 border-gray-300 p-3 shadow">
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Status</p>
                      <p className="text-sm font-bold text-gray-900 uppercase">{selectedProperty.property_status || 'N/A'}</p>
                    </div>
                    <div className="bg-white rounded-lg border-2 border-gray-300 p-3 shadow">
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Ownership</p>
                      <p className="text-sm font-bold text-gray-900 capitalize">{selectedProperty.ownership_type || 'N/A'}</p>
                    </div>
                    <div className="bg-white rounded-lg border-2 border-gray-300 p-3 shadow">
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Year Built</p>
                      <p className="text-sm font-bold text-gray-900">{selectedProperty.year_built || 'N/A'}</p>
                    </div>
                    <div className="bg-white rounded-lg border-2 border-gray-300 p-3 shadow">
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Purchase Date</p>
                      <p className="text-sm font-bold text-gray-900">
                        {selectedProperty.purchase_date ? parseCalendarDay(selectedProperty.purchase_date).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg border-2 border-gray-300 p-3 shadow">
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Purchase Price</p>
                      <p className="text-sm font-bold text-gray-900">
                        {selectedProperty.purchase_price ? `$${parseFloat(selectedProperty.purchase_price).toLocaleString()}` : 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Financial Summary */}
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-600 p-4 shadow-md">
                    <p className="text-xs text-gray-600 uppercase tracking-wider font-semibold mb-3">Financial Information</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-600 uppercase tracking-wider">Current Value</p>
                        <p className="text-lg font-bold text-green-700">
                          ${parseFloat(selectedProperty.current_value || 0).toLocaleString()}
                        </p>
                      </div>
                      {selectedProperty.monthly_rent && parseFloat(selectedProperty.monthly_rent) > 0 && (
                        <div>
                          <p className="text-xs text-gray-600 uppercase tracking-wider">Monthly Rent</p>
                          <p className="text-lg font-bold text-blue-700">
                            ${parseFloat(selectedProperty.monthly_rent).toLocaleString()}
                          </p>
                        </div>
                      )}
                      {selectedProperty.ownership_type === 'mortgaged' && (
                        <>
                          <div>
                            <p className="text-xs text-gray-600 uppercase tracking-wider">Mortgage Balance</p>
                            <p className="text-lg font-bold text-red-700">
                              {selectedProperty.mortgage_balance ? `$${parseFloat(selectedProperty.mortgage_balance).toLocaleString()}` : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 uppercase tracking-wider">Monthly Payment</p>
                            <p className="text-base font-bold text-gray-900">
                              {selectedProperty.mortgage_payment ? `$${parseFloat(selectedProperty.mortgage_payment).toLocaleString()}` : 'N/A'}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                    {selectedProperty.ownership_type === 'mortgaged' && selectedProperty.mortgage_lender && (
                      <div className="mt-3 pt-3 border-t border-green-300">
                        <p className="text-xs text-gray-600 uppercase tracking-wider">Mortgage Lender</p>
                        <p className="text-sm font-semibold text-gray-900">{selectedProperty.mortgage_lender}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes Section (if exists) */}
              {selectedProperty.notes && (
                <div className="mt-6 bg-yellow-50 rounded-lg border-2 border-yellow-400 p-4 shadow">
                  <p className="text-xs text-gray-600 uppercase tracking-wider font-semibold mb-2">Additional Notes</p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{selectedProperty.notes}</p>
                </div>
              )}
            </div>

            {/* ID Card Footer */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-8 py-3 rounded-b-xl border-t-2 border-yellow-500">
              {/* Blank footer bar */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Properties;
